import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import useRouting from "../hooks/useRouting";
import { trackEvent } from "../utils/trackEvent";
import environment from "../environment";
import axios from "axios";
import { setStorage, getStorage, getHeaders } from "../utils/apiUtils";
import "./Login.css";
import Header from "./Header";
import MedicalBill from "../assets/images/MedicalBill.svg";
import lock from "../assets/images/lock.svg";
import ProtectShield from "../assets/images/ProtectShield.svg";
import Agreed from "../assets/images/Agreed.svg";
import InstantApproval from "../assets/images/InstantApproval.svg";
import SaveIcon from "../assets/images/SaveIcon.svg";
import QualityTreatment from "../assets/images/QualityTreatment.svg";

const Login = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [searchParams] = useSearchParams();
  const { navigateWithContext, anchor } = useRouting();
  const hasAuthenticatedRef = useRef(false);

  useEffect(() => { trackEvent("loan_login_viewed"); }, []);

  // Handle query parameters on component load
  useEffect(() => {
    const params = Object.fromEntries(searchParams);
    
    // Check for phone number in localStorage and prefill if exists
    const storedPhoneNumber = localStorage.getItem("phoneNumber");
    if (storedPhoneNumber) {
      setPhoneNumber(storedPhoneNumber);
    }

    // Handle key parameter for authentication
    // Use ref to prevent duplicate calls in StrictMode
    if (params.key && params.key !== "" && !hasAuthenticatedRef.current) {
      hasAuthenticatedRef.current = true;
      
      const authenticateWithKey = async () => {
        try {
          const response = await axios.post(`${environment.apiurl}auth/`, {
            access_token: params.key,
          });

          if (response.data && response.data.msg) {
            // Store authentication data
            setStorage("token", response.data.msg.token);
            setStorage("name", response.data.msg.name);
            setStorage("application_id", response.data.msg.application_id);

            // Call decision function inline to avoid dependency issues
            const handleDecision = async () => {
              try {
                const applicationId = getStorage("application_id");
                if (!applicationId) {
                  console.error("Application ID not found");
                  return;
                }

                const statusResponse = await axios.get(
                  `${environment.apiurl}applications/${applicationId}/ui_management_multi/loan/status/`,
                  { headers: getHeaders() }
                );

                if (statusResponse.data && statusResponse.data.msg) {
                  const status = statusResponse.data.msg.status;

                  // Replace the Login?key entry so back from the first loan screen leaves the module
                  // instead of landing on this auto-login screen again.
                  // if (status === "1") {
                  //   navigateWithContext(`loan-type`);
                  // }
                  //  else {
                  //   navigateWithContext(status);
                  // }
                  if (status === "1") {
                    navigateWithContext(`loan-type`, { replace: true });
                  }
                   else {
                    navigateWithContext(status, { replace: true });
                  }
                }
              } catch (error) {
                console.error("Decision error:", error);
                alert("Error");
              }
            };

            await handleDecision();
          }
        } catch (error) {
          console.error("Authentication error:", error);
          alert("Error");
          hasAuthenticatedRef.current = false; // Reset on error to allow retry
        }
      };

      authenticateWithKey();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Only depend on searchParams

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Only allow digits
    if (value.length <= 10) {
      setPhoneNumber(value);
    }
  };

  const handleGetOTP = async () => {
    trackEvent("loan_login_submit_clicked", { phone_valid: phoneNumber.length === 10 });
    if (phoneNumber.length === 10) {
      try {
        // Prepare payload
        const payload = {
          mobile: phoneNumber,
          anchor: anchor.toUpperCase(),
        };

        // Call API to send OTP
        const response = await axios.post(
          `${environment.apiurl}auth/get-otp-generic/`,
          payload
        );

        // Check if response is successful
        if (response?.data && response?.data?.msg) {
          // Store phone number in localStorage for next screen
          localStorage.setItem("phoneNumber", phoneNumber);

          // Use new routing
          const params = Object.fromEntries(searchParams);
          const queryString = new URLSearchParams(params).toString();
          const newPath = queryString ? `otp?${queryString}` : "otp";
          navigateWithContext(newPath);
        } else {
          // Handle API error response
          const errorMessage =
            response.data?.message || "Failed to send OTP. Please try again.";
          alert(errorMessage);
        }
      } catch (error) {
        console.error("Error sending OTP:", error);

        // Handle different types of errors
        let errorMessage = "Failed to send OTP. Please try again.";

        if (error.response) {
          // Server responded with error status
          errorMessage =
            error.response.data?.message ||
            `Server Error: ${error.response.status}`;
        } else if (error.request) {
          // Network error
          errorMessage =
            "Network error. Please check your internet connection.";
        }

        alert(errorMessage);
      }
    }
  };

  const isValidPhone = phoneNumber.length === 10;

  return (
    <div className="loan-container">
      <Header showBackButton={false} />

      <div className="main-content">
        <div className="hero-card">
          <div className="hero-section">
            <h1 className="hero-title">
              We will pay your
              <br />
              Hospital Bills
            </h1>
            <p className="hero-subtitle">
              Get instant medical loans
              <br />
              at zero cost
            </p>
            <div className="hero-icon">
              <img src={MedicalBill} alt="Medical Bill" />
            </div>
          </div>
        </div>

        <div className="features-card">
          <ul className="features-list">
            <li className="feature-item">
              <div className="feature-icon">
                <img src={SaveIcon} alt="QualSave Icon" />
              </div>
              <span className="feature-title">
                Save up to ₹10,000 on <br /> interest cost
              </span>
            </li>
            <li className="feature-item">
              <div className="feature-icon">
                <img src={QualityTreatment} alt="Quality Treatment" />
              </div>
              <span className="feature-title">
                Quality treatment at your preferred network hospital
              </span>
            </li>
            <li className="feature-item">
              <div className="feature-icon">
                <img src={InstantApproval} alt="Instant Approval" />
              </div>
              <span className="feature-title">
                Instant approval for medical expenses up to ₹3 lakhs
              </span>
            </li>
          </ul>
        </div>

        <div className="form-card">
          <div className="form-section">
            <h2 className="form-title-login">
              Please enter your mobile no. to apply
            </h2>

            <div className="phone-input-container">
              <div className="country-code">+91</div>
              <input
                type="tel"
                className={`phone-input ${phoneNumber ? "filled" : ""}`}
                placeholder="Enter 10-digit mobile number"
                value={phoneNumber}
                onChange={handlePhoneChange}
                maxLength={10}
              />
            </div>

            <div className="disclaimer">
              <div className="disclaimer-item">
                <img className="disclaimer-icon" src={lock} alt="lock" />
                <span className="disclaimer-title">
                  We use this to send you OTP and updates. Your data is secure.
                </span>
              </div>
              <div className="disclaimer-item">
                <img
                  className="disclaimer-icon"
                  src={ProtectShield}
                  alt="Protect Shield"
                />
                <span className="disclaimer-title">
                  We protect your data with bank-grade security.
                </span>
              </div>
              <div className="disclaimer-item">
                <img className="disclaimer-icon" src={Agreed} alt="Agreed" />
                <span className="disclaimer-title">
                  By continuing, you agree to AyushPay&apos;s{" "}
                  <a
                    href="https://www.ayushpay.com/termsCondition?_gl=1*1m5c89q*_ga*MTI3ODg5MjMwOC4xNzMyNjk3NDY5*_ga_6935DYTJ2E*czE3NTg3OTk0OTQkbzUkZzAkdDE3NTg3OTk0OTUkajU5JGwwJGgw"
                    className="privacy-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://www.ayushpay.com/privacyPolicy?_gl=1*jrkhot*_ga*MTI3ODg5MjMwOC4xNzMyNjk3NDY5*_ga_6935DYTJ2E*czE3NTg3OTk0OTQkbzUkZzAkdDE3NTg3OTk0OTckajU3JGwwJGgw"
                    className="privacy-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                  .
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed-button-container">
        <button
          className="continue-button"
          onClick={handleGetOTP}
          disabled={!isValidPhone}
        >
          GET OTP
        </button>
      </div>
    </div>
  );
};

export default Login;
